import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, current } from '@reduxjs/toolkit';
import { omit } from 'lodash-es';

import type { MedCollectionMode } from '@shared/services/medrefill/types/commons.type';
import type { Clinic } from '@shared/services/medrefill/types/getClinicList.type';
import type { MedRefillInstitution } from '@shared/services/medrefill/types/getMedRefillInstitutions.type';
import type { PrescriptionSummary } from '@shared/services/medrefill/types/getPrescriptionList.type';
import type {
  SubmitMedRefillErrorResult,
  SubmitMedRefillResult,
} from '@shared/services/medrefill/types/submitMedRefillRequest';

import { initialState } from './initialState';
import type {
  EditableMedRefillPaymentMode,
  IntegratedMedicationOptions,
  MedicationCollection,
  MedicationOption,
  MedRefillState,
  PatientDetails,
  PrescribedMedications,
  SavePreviousData,
  SelectedOrderType,
  SheetData,
  SortAndFilterData,
  TopUpMedication,
} from '../types/medrefill.types';

const medRefillSlice = createSlice({
  initialState,
  name: 'MedRefill',
  reducers: {
    beginSummaryEdit: (state) => {
      const snapshot: MedRefillState = current(state);
      state.editSnapshot = omit(snapshot, ['editSnapshot', 'sheetDatas']);
    },
    clearCollectionDetails: (state) => {
      state.collectionDetails = null;
    },
    clearIntegratedRefillOptions: (state) => {
      state.integratedRefillOptions = null;
    },
    clearMedicationRefillOptions: (state) => {
      state.refillMedicationOption = null;
    },
    clearOriginalPrescriptionDate: (state) => {
      state.originalPrescriptionDate = null;
    },
    clearPatientDetails: (state) => {
      state.patientDetails = null;
    },
    clearPrescribedMedications: (state) => {
      state.prescribedMedications = null;
    },
    clearPrescriptionSummary: (state) => {
      state.prescriptionSummary = null;
    },
    clearPreviousSavedPrescriptionSummary: (state) => {
      state.previousSavedPrescriptionSummary = null;
    },
    clearSelectedInstitution: (state) => {
      state.selectedInstitution = null;
    },
    clearSheetData: (state) => {
      state.sheetDatas = [];
    },
    clearSortFilterData: (state) => {
      state.sortFilterData = null;
    },
    commitSummaryEdit: (state) => {
      state.editSnapshot = null;
    },
    resetRefillState: (state) => {
      state.originalPrescriptionDate = null;
      state.sheetDatas = [];
      state.selectedMedOrderType = null;
      state.patientDetails = null;
      state.paymentInfo = initialState.paymentInfo;
      state.refillMedicationOption = null;
      state.collectionDetails = null;
      state.prescribedMedications = null;
      state.sortFilterData = null;
      state.submitMedRefill = initialState.submitMedRefill;
      state.integratedRefillOptions = null;
      state.isEditSummary = false;
      state.isInvalidCollectionMode = false;
      state.editSnapshot = null;
      state.commonNonSupportedCollectionModes = [];
      state.commonSupportedCollectionModes = [];
      state.previousSavedData = null;
      state.specialInstructions = initialState.specialInstructions;
      state.prescriptionSummary = null;
      state.selectedClinic = initialState.selectedClinic;
      state.topUpMedicationDetails = initialState.topUpMedicationDetails;
    },
    resetRefillStateForResubmit: (state) => {
      state.originalPrescriptionDate = null;
      state.sheetDatas = [];
      state.selectedMedOrderType = null;
      state.paymentInfo = initialState.paymentInfo;
      state.refillMedicationOption = null;
      state.collectionDetails = null;
      state.prescribedMedications = null;
      state.sortFilterData = null;
      state.submitMedRefill = initialState.submitMedRefill;
      state.integratedRefillOptions = null;
      state.isEditSummary = false;
      state.isInvalidCollectionMode = false;
      state.editSnapshot = null;
      state.commonNonSupportedCollectionModes = [];
      state.commonSupportedCollectionModes = [];
      state.previousSavedData = null;
      state.specialInstructions = initialState.specialInstructions;
      state.prescriptionSummary = null;
      state.previousSavedPrescriptionSummary = null;
      state.selectedClinic = initialState.selectedClinic;
      state.topUpMedicationDetails = initialState.topUpMedicationDetails;
    },
    rollbackSummaryEdit: (state) => {
      if (!state.editSnapshot) {
        state.isEditSummary = false;
        return state;
      }

      return { ...current(state), ...current(state.editSnapshot), editSnapshot: null, isEditSummary: false };
    },
    savePreviousData: (state, action: PayloadAction<SavePreviousData>) => {
      if (!state.previousSavedData) {
        state.previousSavedData = {};
      }
      state.previousSavedData[action.payload.key] = action.payload.value;
    },
    setCollectionDetails: (state, action: PayloadAction<MedicationCollection>) => {
      state.collectionDetails = action.payload;
    },
    setIntegratedRefillOptions: (state, action: PayloadAction<IntegratedMedicationOptions>) => {
      state.integratedRefillOptions = action.payload;
    },
    setIsEditSummary: (state, action: PayloadAction<boolean>) => {
      state.isEditSummary = action.payload;
    },
    setCommonNonSupportedCollectionModes: (state, action: PayloadAction<MedCollectionMode[]>) => {
      state.commonNonSupportedCollectionModes = action.payload;
    },
    setCommonSupportedCollectionModes: (state, action: PayloadAction<MedCollectionMode[]>) => {
      state.commonSupportedCollectionModes = action.payload;
    },
    setIsInvalidCollectionMode: (state, action: PayloadAction<boolean>) => {
      state.isInvalidCollectionMode = action.payload;
    },
    setMedicationRefillOptions: (state, action: PayloadAction<MedicationOption>) => {
      state.refillMedicationOption = action.payload;
    },
    setOriginalPrescriptionDate: (state, action: PayloadAction<string>) => {
      state.originalPrescriptionDate = action.payload;
    },
    setPatientDetails: (state, action: PayloadAction<PatientDetails>) => {
      state.patientDetails = action.payload;
    },
    setPaymentInstructions: (state, action: PayloadAction<string>) => {
      state.paymentInfo.paymentInstructions = action.payload;
    },
    setPrescribedMedications: (state, action: PayloadAction<PrescribedMedications>) => {
      state.prescribedMedications = action.payload;
    },
    setPrescriptionSummary: (state, action: PayloadAction<PrescriptionSummary>) => {
      state.prescriptionSummary = action.payload;
    },
    setPreviousSavedPrescriptionSummary: (state, action: PayloadAction<null | PrescriptionSummary>) => {
      state.previousSavedPrescriptionSummary = action.payload;
    },
    setReasonForTopUpMedication: (state, action: PayloadAction<{ elaboration?: string; reason: string }>) => {
      state.topUpMedicationDetails.reasonForTopUp = {
        elaboration: action.payload.elaboration ?? '',
        reason: action.payload.reason,
      };
    },
    setSelectedClinic: (state, action: PayloadAction<Clinic>) => {
      state.selectedClinic = action.payload;
    },
    setSelectedInstitution: (state, action: PayloadAction<MedRefillInstitution>) => {
      state.selectedInstitution = action.payload;
    },
    setSelectedMedOrderType: (state, action: PayloadAction<SelectedOrderType>) => {
      state.selectedMedOrderType = action.payload;
    },
    setSelectedPaymentModes: (state, action: PayloadAction<EditableMedRefillPaymentMode[]>) => {
      state.paymentInfo.paymentModes = action.payload;
    },
    setSheetData: (state, action: PayloadAction<SheetData[]>) => {
      state.sheetDatas = action.payload;
    },
    setSortFilterData: (state, action: PayloadAction<SortAndFilterData>) => {
      state.sortFilterData = action.payload;
    },
    setSpecialInstructions: (state, action: PayloadAction<string>) => {
      state.specialInstructions = action.payload;
    },
    setSubmitMedRefillResult: (
      state,
      action: PayloadAction<{ error?: SubmitMedRefillErrorResult; result: null | SubmitMedRefillResult }>
    ) => {
      if (action.payload.error) {
        state.submitMedRefill.error = action.payload.error;
      } else state.submitMedRefill.result = action.payload.result;
    },
    setTopUpMedicationData: (state, action: PayloadAction<TopUpMedication[]>) => {
      state.topUpMedicationDetails.topUpMedicationList = action.payload;
    },
  },
});

export const { actions: medRefillAction } = medRefillSlice;

export default medRefillSlice.reducer;
